import React, { useMemo, useState } from "react";
import { ClipboardList, UserRound } from "lucide-react";
import PatientRegistration from "./PatientRegistration";
import TestOrder from "./TestOrder";

const PatientRegistrationFlow = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [patientData, setPatientData] = useState(null);

  const steps = useMemo(
    () => [
      {
        id: 1,
        title: "Patient",
        subtitle: "Create or confirm patient details",
        Icon: UserRound,
      },
      {
        id: 2,
        title: "Order tests",
        subtitle: "Select tests and answer required questions",
        Icon: ClipboardList,
      },
    ],
    []
  );

  const progressPct = currentStep === 1 ? 50 : 100;

  const handlePatientRegistered = (patient) => {
    setPatientData(patient);
    setCurrentStep(2);
  };

  const handleOrderComplete = () => {
    // Reset the flow or redirect to another page
    setCurrentStep(1);
    setPatientData(null);
  };

  const handleBackToPatientInfo = () => {
    setCurrentStep(1);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6">
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70">
          <div className="relative">
            <div className="h-2 w-full rounded-t-2xl bg-gradient-to-r from-[#235F72] via-[#085DB6] to-[#235F72]" />
            <div className="px-5 py-5 sm:px-6">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-wide text-gray-600">
                    Reception
                  </div>
                  <div className="mt-1 text-xl font-semibold text-gray-900">
                    Patient registration & lab ordering
                  </div>
                  <div className="mt-1 text-sm text-gray-600">
                    A calm, guided 2‑step flow for check‑in and test requests.
                  </div>
                </div>

                {patientData ? (
                  <div className="flex min-w-0 flex-wrap items-center gap-2 rounded-2xl bg-gray-50 px-3 py-2 ring-1 ring-gray-200">
                    <span className="inline-flex items-center rounded-full bg-[#36F1A2]/15 px-2.5 py-1 text-xs font-semibold text-[#235F72] ring-1 ring-[#36F1A2]/40">
                      Patient
                    </span>
                    <div className="min-w-0 truncate text-sm font-semibold text-gray-900">
                      {patientData?.full_name || "—"}
                    </div>
                    <div className="flex-none text-xs text-gray-600">
                      Card: {patientData?.card_number || "—"}
                    </div>
                  </div>
                ) : null}
              </div>

              <div className="mt-5">
                <div className="flex items-center justify-between gap-3">
                  {steps.map((s) => {
                    const isActive = s.id === currentStep;
                    const isDone = currentStep > s.id;
                    const Icon = s.Icon;

                    return (
                      <div
                        key={s.id}
                        className="flex min-w-0 flex-1 items-center gap-3"
                        aria-current={isActive ? "step" : undefined}
                      >
                        <div
                          className={
                            "inline-flex h-10 w-10 flex-none items-center justify-center rounded-xl ring-1 transition " +
                            (isActive
                              ? "bg-[#235F72] text-white ring-[#235F72]"
                              : isDone
                                ? "bg-[#36F1A2]/15 text-[#235F72] ring-[#36F1A2]/50"
                                : "bg-gray-50 text-gray-600 ring-gray-200")
                          }
                        >
                          <Icon className="h-5 w-5" />
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-900">
                            {s.title}
                          </div>
                          <div className="text-xs text-gray-600">{s.subtitle}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="mt-4 h-2 w-full overflow-hidden rounded-full bg-gray-100 ring-1 ring-gray-200">
                  <div
                    className="h-full rounded-full bg-[#36F1A2] transition-[width] duration-300"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 pb-6 sm:px-6">
            {currentStep === 1 && (
              <PatientRegistration onPatientRegistered={handlePatientRegistered} />
            )}
            {currentStep === 2 && patientData && (
              <TestOrder
                patient={patientData}
                onOrderComplete={handleOrderComplete}
                onBack={handleBackToPatientInfo}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientRegistrationFlow;
